import type { ThemeConfig } from 'antd';

/**
 * The church's colours, in one place (docs/07-implementation-roadmap.md F0.2).
 *
 * These are AntD design tokens, not CSS — components read them through
 * `ConfigProvider`, so changing a value here changes every button, tag and
 * table header at once. No component hard-codes a colour.
 *
 * **These are no longer a placeholder.** The palette is `gkiserpong.org`, the
 * church's own site: burnt sienna, the tan of its navigation band, warm cream
 * behind the content, and its three typefaces. Staff who have seen the church's
 * website should recognise this as the same church without being told.
 *
 * ### Adapted, not copied — and the reason is measured
 *
 * A marketing page and a data-entry app do not get to use the same colours at
 * the same values, because the website spends its brand colour on 55px display
 * headings and this app spends it on 14px labels and 12px tags.
 *
 * - The site's sienna is **#b65f23**. On white that is **4.52:1** — over the AA
 *   line by 0.02, which is not a margin. `BRAND` below is one step darker and
 *   scores 5.64:1. #b65f23 is kept for exactly one thing, the signed-out card's
 *   27px/700 title, where WCAG's large-text threshold is 3:1 and it is
 *   comfortable rather than borderline.
 * - The site's navigation band, **#d19a7a**, cannot carry white text at all
 *   (2.44:1). The site draws black on it. Here it is decoration only — rules,
 *   tints, the stripe on the signed-out card — and never a text background.
 * - The site's cream is **#ede4da**. As a full-page background under 14px
 *   secondary text and placeholders it lands at 4.47–4.49:1, just under. The
 *   page uses **#f4eee8**, a shade lighter, which clears everything with room
 *   (4.60–5.50:1); #ede4da stays for the signed-out field, where nothing small
 *   is drawn on it.
 *
 * Every ratio above was computed, not eyeballed, and `e2e/accessibility.spec.ts`
 * re-measures the rendered result on every run.
 */

/** The site's sienna, one step darker so it can carry 14px text. 5.64:1 on white. */
const BRAND = '#a0521e';
/** The site's exact sienna. Large text only — 4.52:1, fine at 27px/700, not at 14px. */
export const BRAND_DISPLAY = '#b65f23';
/** The navigation band's tan. **Decoration only** — 2.44:1 is not a text colour. */
export const BRAND_TAN = '#d19a7a';
/** The site's cream, kept for surfaces that carry no small text. */
export const BRAND_CREAM = '#ede4da';

export const theme: ThemeConfig = {
  token: {
    /**
     * The site runs three typefaces, and so does this: **Archivo** for the
     * brand lockup, **Raleway** for headings, **Roboto** for body and tables.
     * This token is the body font; Raleway is applied to `h1`–`h5` in
     * `index.css` because AntD has no heading-font token (it exposes exactly
     * `fontFamily` and `fontFamilyCode`), and Archivo is named by the two
     * components in `components/brand.tsx` that draw the lockup.
     *
     * **Self-hosted is not a preference here, it is the only option.** The
     * document CSP is `font-src 'self'` (`index.html`), so the
     * `fonts.googleapis.com` stylesheet the site itself uses would be blocked
     * outright and every screen would silently fall back to the system sans.
     * `src/main.tsx` imports four weights out of @fontsource — ~81 kB of woff2,
     * all `font-display: swap`, none of it on the JS budget.
     */
    fontFamily: "Roboto, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",

    colorPrimary: BRAND,

    /*
     * Info is the one token that is **deliberately not warm.**
     *
     * AntD derives every info surface from it, and a brown-tinted info panel in
     * a brown-primary app stops reading as informational and starts reading as
     * a primary action — the same argument that kept this token off the brand
     * colour when the brand was teal, and then blue. A blue notice against a
     * warm palette is unmistakable, which is the entire job.
     */
    colorInfo: '#1677ff',

    // Success and warning are the *exact* pairs `membership-status.ts` measured
    // for the status tags, rather than fresh values that would put two greens
    // and two golds on the same screen. Green 5.59:1 on white, gold 6.79:1.
    colorSuccess: '#237804',
    colorWarning: '#874d00',
    colorError: '#b3261e',

    /*
     * Two contrast corrections from the F7.3 pass, both measured rather than
     * eyeballed — `e2e/accessibility.spec.ts` re-measures them on every run.
     *
     * **Secondary text.** AntD's `colorTextDescription` is `rgba(0,0,0,0.45)`,
     * which composites to #8c8c8c and scores **3.36:1** on white — under the
     * 4.5:1 that WCAG AA asks for body text. It is not a marginal amount of the
     * app: every screen's explanatory paragraph, every "X jemaat ditemukan",
     * the timezone note on the birthday report. 0.6 scores 5.74:1 on white and,
     * on the three warm surfaces this palette introduces, 5.50:1 on the page,
     * 5.46:1 on the table header and 5.45:1 on the selected menu row — the
     * darkest grounds any of it is drawn on. It stays visibly lighter than the
     * 0.88 body text, so the hierarchy the token exists for survives.
     *
     * `colorTextSecondary` is deliberately left alone: its default (0.65) is
     * already 7:1, and lowering it to match would be a regression.
     */
    colorTextDescription: 'rgba(0, 0, 0, 0.6)',

    /*
     * **Links stay blue, and now for a stronger reason than before.**
     *
     * The rule has always been that a link must not be the colour of the
     * primary button, or it stops reading as a link. When the brand was blue
     * that rule was awkward — it had to be kept by lightness alone. A warm
     * primary makes it easy again: blue is the one link convention nobody has
     * to learn, and against sienna buttons it can never be mistaken for one.
     *
     * 6.16:1 on white and 5.35:1 on the cream page. Hover goes *darker* rather
     * than lighter, which is the opposite of AntD's derivation — a hover state
     * that loses contrast is a control that gets harder to read at the moment
     * it is being used.
     */
    colorLink: '#0958d9',
    colorLinkHover: '#003eb3',
    colorLinkActive: '#002c8c',

    /*
     * **Placeholders.** AntD's `colorTextPlaceholder` is `rgba(0,0,0,0.25)` —
     * #bfbfbf, **1.83:1**, barely visible and nowhere near AA.
     *
     * It matters more here than the word "placeholder" suggests, because in
     * this app the placeholder is usually *the current state of a filter*:
     * every Select on the member list reads "Semua status", "Semua wilayah",
     * "Semua alasan" when nothing is chosen. Someone who cannot read that
     * cannot tell a filtered list from an unfiltered one — which on a screen
     * whose whole job is finding people is a wrong answer, not a cosmetic
     * problem. 0.55 clears AA at 4.74:1 on white and 4.60:1 on the cream, and
     * still sits clearly below a real value's 0.88.
     */
    colorTextPlaceholder: 'rgba(0, 0, 0, 0.55)',

    // Slightly denser than AntD's default: these screens are tables and forms,
    // and the office reads them on 1366×768 laptops.
    fontSize: 14,

    /*
     * Bold rather than AntD's 600, because only two Roboto weights are shipped
     * and 600 between them would be **synthesised** — the browser smearing 400
     * into a fake bold, which is exactly as ugly as it sounds on a table header
     * repeated down every screen. 400 and 700 are real files; this token makes
     * the app ask for the one it has.
     */
    fontWeightStrong: 700,

    /*
     * The site is a Bootstrap theme: square navigation, lightly rounded content
     * cards. Squarer than the 10px/28px the previous palette used, which came
     * from a different property and read softer than this one does.
     */
    borderRadius: 6,
    borderRadiusLG: 10,

    /*
     * `size="large"` only — the four signed-out screens and the dialogs. The
     * default `controlHeight` is left at 32 on purpose: the member list's filter
     * row and every table on those 1366×768 laptops are sized by it, and 44px
     * there would cost a row of results per screen to match a login form.
     */
    controlHeightLG: 44,

    colorBgLayout: '#f4eee8',
    colorBorderSecondary: '#e6dbcd',
  },
  components: {
    Layout: {
      // White, like the strip the site puts its logo in above the tan band.
      headerBg: '#ffffff',
      headerHeight: 56,
      siderBg: '#ffffff',
    },
    Table: {
      // The cream one step deeper, so a header reads as part of the same warm
      // family rather than as the neutral grey it used to be.
      headerBg: '#f3ebe1',
      cellPaddingBlock: 10,
    },
    Menu: {
      /*
       * The selected navigation item, corrected in the F7.3 pass and re-derived
       * for each palette since.
       *
       * AntD derives the selected item's background from `colorPrimary`, and
       * that derivation is what the correction exists for: on the original teal
       * it produced a mid grey carrying **3.59:1** text, making the *one* item
       * that says where you are the least readable thing in the sidebar. A pale
       * tint of the brand hue instead, with the label in a darker sienna than
       * the brand so it holds up against the tint. **5.55:1.**
       */
      itemSelectedBg: '#f5e9dc',
      itemSelectedColor: '#8f4a1b',
    },
    Card: {
      // Warmed, and wider than AntD's default: a white card on cream needs a
      // shadow to sit *on* the page rather than look like a hole cut in it.
      boxShadowTertiary: '0 8px 24px rgba(122, 74, 40, 0.08)',
    },
  },
};
